/**
 * Where the app's chrome (header + global search) must NOT render.
 *
 * ONE list, used by BOTH headers — the consumer `components/layout/header.tsx`
 * and the recruiter `components/admin/admin-header.tsx`. They were always going
 * to need the same answer, and two hand-maintained copies of a suppression list
 * drift in the direction that matters least visibly: a surface that should have
 * been bare quietly grows navigation back.
 *
 * WHY THIS IS A CORRECTNESS RULE, NOT A LAYOUT PREFERENCE.
 * On a live proctored assessment or a live interview the header is not chrome,
 * it is an exit. It offers a candidate mid-session a one-click route out and,
 * once global search lands, a box that surfaces unrelated records. The vault
 * routes below were NOT previously suppressed — `/vault/assessment/[sessionId]`
 * mounts `useProctoringSystem` and uses `min-h-screen` (not `fixed inset-0`),
 * so the consumer header has been rendering above a live proctored assessment.
 * That is a pre-existing gap this module closes, independent of search.
 *
 * NOTE ON THE INTERVIEW RULE. It matches the room EXACTLY — one path segment —
 * so `/interview/[roomId]/review` and `/interview/[roomId]/candidate-review`
 * keep their chrome. That is deliberate: those are post-session review
 * surfaces, not live ones, and a recruiter reading a recording back wants the
 * nav. Do not "fix" this into a prefix match.
 *
 * Adding a surface here is a behaviour change to a live page. Add the route to
 * the table below AND to `app-chrome-visibility.test.ts`, which enumerates
 * every suppressed path and every neighbour that must keep its chrome.
 */

/** Exact single-segment live surfaces. */
const LIVE_SESSION_PATTERNS: readonly RegExp[] = [
  // Live H2H video. Full-screen RTK UI; tenant marketing nav breaks immersion
  // and steals real estate from the control bar.
  /^\/interview\/[^/]+$/,
  // Live proctored assessment — mounts useProctoringSystem.
  /^\/vault\/assessment\/[^/]+$/,
  // Assessment session shell / pre-flight: environment checks, identity
  // capture, the consent gate. Leaving mid-flow is the same failure.
  /^\/vault\/session\/[^/]+$/,
  // Arena gameplay. No proctoring hook today, suppressed for focus — a timed
  // game with a nav bar over it is the same interruption class.
  /^\/me\/arena\/play\/[^/]+$/,
];

/** Whole subtrees that own their shell. */
const STANDALONE_PREFIXES: readonly string[] = [
  '/auth',
  '/signup',
  '/request-access',
  // The apply flow renders its OWN fixed header with an integrated progress
  // bar; the global header on top produced a double header plus a phantom
  // marketing footer on /apply/[jobId] (and its /success child).
  '/apply',
];

/**
 * True when the app's header/search must not render on `path`.
 *
 * `/admin` is deliberately NOT here. The two headers disagree about it — the
 * consumer header hides (the admin console has its own), the admin header is
 * the one doing the rendering — so that split stays at the call site where it
 * is legible, and this module answers only the question both share.
 *
 * Defensive on null: `usePathname()` can be null during the very first SSR
 * pass, and a standalone route must not flash chrome for a frame.
 */
export function shouldHideAppChrome(path: string | null | undefined): boolean {
  const p = path ?? '';
  if (STANDALONE_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`))) {
    return true;
  }
  return LIVE_SESSION_PATTERNS.some((pattern) => pattern.test(p));
}

/** The suppressed surfaces, for tests and for anyone auditing the rule. */
export const __chromeSuppression = { LIVE_SESSION_PATTERNS, STANDALONE_PREFIXES };
